export function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic soft accent color for avatars without a photo.
const AVATAR_HUES = [
  "bg-primary-100 text-primary-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
  "bg-cyan-100 text-cyan-700",
];

export function avatarColor(seed = "") {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_HUES[Math.abs(hash) % AVATAR_HUES.length];
}

// Best-effort array coercion: the bio fields are sometimes stored as JSON
// strings, sometimes as real arrays, depending on how they were last saved.
export function coerceArray(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch {
      return value.length ? [value] : [];
    }
  }
  return [];
}

// Shared by formatSmartTimestamp and groupByRecency below — pulled out
// rather than duplicated in both places.
export function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// A precise, tiered timestamp instead of a vague, ever-changing "3 hours
// ago" — used for every post, comment, message, and notification in the
// app. Always shows an actual time, with just enough date context added
// as something gets older:
//   Today            -> "3:45 PM"
//   Yesterday        -> "Yesterday at 3:45 PM"
//   Earlier this year -> "Jul 29 at 3:45 PM"
//   Older             -> "Jul 29, 2025"
export function formatSmartTimestamp(input) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (isSameDay(date, now)) {
    return time;
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(date, yesterday)) {
    return `Yesterday at ${time}`;
  }

  const monthDay = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  if (date.getFullYear() === now.getFullYear()) {
    return `${monthDay} at ${time}`;
  }

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Buckets a list of dated items into "Today" / "Yesterday" / "Earlier"
// sections — the standard way a real inbox stays scannable as it grows,
// instead of one flat, undifferentiated list. Returns only the sections
// that actually have items, in order, so an empty "Yesterday" heading
// never shows up.
export function groupByRecency(items, getDate) {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const buckets = { Today: [], Yesterday: [], Earlier: [] };
  items.forEach((item) => {
    const date = new Date(getDate(item));
    if (isSameDay(date, now)) buckets.Today.push(item);
    else if (isSameDay(date, yesterday)) buckets.Yesterday.push(item);
    else buckets.Earlier.push(item);
  });

  return Object.entries(buckets).filter(([, list]) => list.length > 0);
}
